import React from 'react'
import { memo, MouseEvent, useCallback, useContext, useMemo, useState } from 'react'
import { ActionButton } from 'src/components/shared/ActionButton'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { CirclePlusMinor, InviteMinor, LinkMinor, SaveMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { BasicModal } from 'src/components/shared/modal/BasicModal'
import { InviteWorkersModalType } from './enums'
import { MenuActionItem } from 'src/interfaces/ui'
import { ComponentType } from 'react'
import { InviteWorkersViaMessageForm } from './invite-workers-via-message-form/InviteWorkersViaMessageForm'
import { InviteWorkersViaLinkForm } from './invite-workers-via-link-form/InviteWorkersViaLinkForm'
import { InviteWorkersViaCSVForm } from './invite-workers-via-csv-form/InviteWorkersViaCSVForm'

type ModalComponent = ComponentType<{ onClose: () => void; onSubmit?: () => void }>

export const INVITE_WORKERS_MODAL_CONFIG = {
  [InviteWorkersModalType.InviteViaMessage]: InviteWorkersViaMessageForm,
  [InviteWorkersModalType.InviteViaLink]: InviteWorkersViaLinkForm,
  [InviteWorkersModalType.UploadCsv]: InviteWorkersViaCSVForm,
}

const InviteWorkersButtonComponent = () => {
  const { reload } = useContext(ReloadWrapperContext)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [activeModal, setActiveModal] = useState<InviteWorkersModalType | null>(null)

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => setAnchorEl(null), [])
  const handleCloseModal = useCallback(() => setActiveModal(null), [])

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    return [
      {
        label: 'Invite contractor via message',
        icon: <InviteMinor width={20} />,
        onClick: () => {
          setActiveModal(InviteWorkersModalType.InviteViaMessage)
          setAnchorEl(null)
        },
      },
      {
        label: 'Invite contractor via link',
        icon: <LinkMinor width={20} />,
        onClick: () => {
          setActiveModal(InviteWorkersModalType.InviteViaLink)
          setAnchorEl(null)
        },
      },
      {
        label: 'Upload CSV',
        icon: <SaveMinor width={20} />,
        onClick: () => {
          setActiveModal(InviteWorkersModalType.UploadCsv)
          setAnchorEl(null)
        },
      },
    ]
  }, [])

  const ActiveModalComponent = activeModal
    ? (INVITE_WORKERS_MODAL_CONFIG[activeModal] as ModalComponent)
    : null

  const isMenuOpen = Boolean(anchorEl)
  const isModalOpen = activeModal && ActiveModalComponent

  return (
    <>
      <ActionButton
        text="Invite contractors"
        selected={!!anchorEl}
        className={tw`px-4`}
        color="dark"
        icon={<CirclePlusMinor fill="white" width={20} />}
        onClick={handleClick}
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        itemClassName={tw`w-[232px]`}
        onClose={handleCloseMenu}
      />
      {isModalOpen && (
        <BasicModal isOpen={true} onClose={handleCloseModal}>
          <ActiveModalComponent
            onSubmit={activeModal !== InviteWorkersModalType.InviteViaLink ? reload : undefined}
            onClose={handleCloseModal}
          />
        </BasicModal>
      )}
    </>
  )
}

export const InviteWorkersButton = memo(InviteWorkersButtonComponent)
